import { useEffect, useMemo, useRef, useState } from "react";
import { useNavigate, useParams, useSearchParams } from "react-router-dom";
import { ArrowLeft, Search, X } from "lucide-react";
import { AllItemsIcon } from "@/lib/allItemsIcon";
import { QrMedia } from "../public/qr/QrMedia";
import { MenuBrowser } from "./MenuBrowser";
import { BigItemCard } from "./ItemCards";
import { VegFilter, vegMatch } from "./HomeSig";
import { Flame } from "lucide-react";

const withCombos = (menu) => [...(menu?.items || []), ...(menu?.combos || []).map((c) => ({ ...c, is_combo: true, base_price: c.combo_price, is_available: true }))];

/** Premium layout: left photo category rail + one large card per row; category click filters reliably. */
const PremiumMenu = ({ menu, cat, setCat, q, ctrl, qtyOf, veg = "all", signature = false }) => {
  const all = useMemo(() => withCombos(menu).filter((i) => vegMatch(i, veg)), [menu, veg]);
  const cats = (menu.categories || []).filter((c) => veg === "all" || all.some((i) => i.category_id === c.id));
  const hotIds = signature ? (menu.hot_selling_item_ids || []) : [];
  const needle = q.trim().toLowerCase();
  const items = all.filter((i) => (needle ? `${i.name} ${i.description || ""}`.toLowerCase().includes(needle) : cat === "all" || (cat === "hot" ? hotIds.includes(i.id) : i.category_id === cat)));
  if (cat === "hot" && !needle) items.sort((a, b) => hotIds.indexOf(a.id) - hotIds.indexOf(b.id));
  const heading = needle ? `Results for “${q.trim()}”` : cat === "all" ? "All Items" : cat === "hot" ? "Hot Selling" : cats.find((c) => c.id === cat)?.name || "Menu";
  const ai = menu.all_items_appearance || {};
  return (
    <div className={`os-premium ${signature ? "sig-menu" : ""}`} data-testid="premium-menu">
      <aside className="os-premium-cats" aria-label="Categories" data-testid="premium-categories">
        <button type="button" className={`os-premium-cat ${cat === "all" && !needle ? "selected" : ""}`} onClick={() => setCat("all")} aria-pressed={cat === "all"} data-testid="premium-category-all">
          {ai.mode === "photo" && ai.image_url ? <QrMedia src={ai.image_url} alt="All Items" size="large" /> : <span className="qr-image-placeholder"><AllItemsIcon appearance={ai} size={22} /></span>}<span>All Items</span>
        </button>
        {hotIds.length > 0 && (
          <button type="button" className={`os-premium-cat ${cat === "hot" && !needle ? "selected" : ""}`} onClick={() => setCat("hot")} aria-pressed={cat === "hot"} data-testid="premium-category-hot">
            <span className="qr-image-placeholder" style={{ color: "#ea580c" }}><Flame size={24} /></span><span>Hot Selling</span>
          </button>
        )}
        {cats.map((c) => (
          <button key={c.id} type="button" className={`os-premium-cat ${cat === c.id && !needle ? "selected" : ""}`} onClick={() => setCat(c.id)} aria-pressed={cat === c.id} data-testid={`premium-category-${c.id}`}>
            <QrMedia src={c.image_url} alt={c.name} /><span>{c.name}</span>
          </button>
        ))}
      </aside>
      <section className="os-premium-items" data-testid="premium-items">
        <h2 data-testid="menu-category-heading">{heading} <small style={{ fontSize: 12, color: "var(--muted)", fontWeight: 600 }} data-testid="menu-result-count">{items.length} items</small></h2>
        <div className="os-premium-grid">{items.map((it) => <BigItemCard key={`${it.is_combo ? "c" : "i"}-${it.id}`} it={it} qty={qtyOf[it.id] || 0} ctrl={ctrl} />)}</div>
        {!items.length && <p className="os-hint" data-testid="menu-empty">No dishes found.</p>}
      </section>
    </div>
  );
};

/** Full menu page: owner-selected layout (Classic = existing MenuBrowser, unchanged; Premium = new). URL carries the category. */
export const FullMenu = ({ slug, menu, layout, cart, ctrl, qtyOf, onCart, veg = "all", setVeg }) => {
  const signature = layout === "signature";
  const nav = useNavigate();
  const { catId } = useParams();
  const [sp] = useSearchParams();
  const initialQuery = sp.get("q") || "";
  const [q, setQ] = useState(initialQuery);
  const [searching, setSearching] = useState(!!initialQuery);
  const inputRef = useRef(null);
  const cat = catId && ((menu.categories || []).some((c) => c.id === catId) || catId === "hot") ? catId : "all";
  const setCat = (id) => { setQ(""); nav(id === "all" ? `/order/${slug}/menu` : `/order/${slug}/menu/${id}`, { replace: true }); window.scrollTo({ top: 0, behavior: "smooth" }); };
  useEffect(() => { if (searching) inputRef.current?.focus(); }, [searching]);
  useEffect(() => { window.scrollTo(0, 0); }, []);

  return (
    <div data-testid={`full-menu-${layout}`}>
      <div className="os-menu-top" data-testid="menu-topbar">
        <button type="button" className="os-icon-btn" style={{ border: 0 }} onClick={() => nav(`/order/${slug}`)} aria-label="Back to home" data-testid="menu-back"><ArrowLeft size={20} /></button>
        <h2>Our Menu</h2>
        <button type="button" className="os-icon-btn" style={{ border: 0 }} onClick={() => { setSearching((s) => !s); if (searching) setQ(""); }} aria-label={searching ? "Close search" : "Search"} data-testid="menu-search-toggle">{searching ? <X size={20} /> : <Search size={20} />}</button>
      </div>
      {signature && <div style={{ padding: "10px 12px 0" }}><VegFilter value={veg} onChange={setVeg} hasNonVeg={!!menu.has_non_veg} testId="menu-veg-filter" /></div>}
      {searching && (layout === "premium" || signature) && (
        <div className="os-search" style={{ margin: "10px 12px 0" }}><Search size={16} /><input ref={inputRef} value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search dishes…" aria-label="Search dishes" data-testid="menu-search" /></div>
      )}
      {layout === "premium" || signature
        ? <PremiumMenu menu={menu} cat={cat} setCat={setCat} q={q} ctrl={ctrl} qtyOf={qtyOf} veg={signature ? veg : "all"} signature={signature} />
        : <div className="qr-experience" style={{ background: "#fff", minHeight: 0 }}><MenuBrowser menu={menu} cart={cart} onOpenItem={ctrl.onOpen} onCart={onCart} initialCat={cat} onCategoryChange={(id) => nav(id === "all" ? `/order/${slug}/menu` : `/order/${slug}/menu/${id}`, { replace: true })} initialQuery={initialQuery} /></div>}
    </div>
  );
};
